import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader, Text } from "@mantine/core";
import { Alert, Card, Button, StatusBadge } from "@xplatform/design-system";
import { IconArrowLeft } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useMonitoring } from "@hooks/useMonitoring";
import { CostBreakdownSection } from "@features/Monitoring";
import type {
    BatchJobSummaryViewModel,
    ExternalRefCapabilityUsageViewModel,
} from "@models/monitoring";

import "./MappingJobDetailPage.scss";

const formatRows = (rowsDone: number | null, rowsTotal: number | null): string => {
    if (rowsDone === null && rowsTotal === null) return "-";
    return `${rowsDone ?? 0}/${rowsTotal ?? 0}`;
};

export const MappingJobDetailPage = () => {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { createdBy, organisationName } =
        (location.state as { createdBy?: string; organisationName?: string }) || {};
    const { fetchMappingJobDetail, fetchProcessCapabilityUsage, error } =
        useMonitoring();
    const [job, setJob] = useState<BatchJobSummaryViewModel | null>(null);
    const [capabilityUsage, setCapabilityUsage] = useState<
        ExternalRefCapabilityUsageViewModel[]
    >([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!jobId) return;
        setLoading(true);
        // Await both fetches together so the shared spinner clears once, rather
        // than flickering when the first of two concurrent calls resolves.
        Promise.all([
            fetchMappingJobDetail(jobId),
            fetchProcessCapabilityUsage(`JOB#${jobId}`),
        ])
            .then(([detail, usage]) => {
                if (detail) setJob(detail);
                if (usage) setCapabilityUsage(usage);
            })
            .finally(() => setLoading(false));
    }, [jobId, fetchMappingJobDetail, fetchProcessCapabilityUsage]);

    const jobErrorMessage =
        job?.error != null
            ? String(job.error.message ?? job.error.description ?? "")
            : "";

    if (loading) {
        return (
            <div className="mapping-job-detail-page">
                <div className="mapping-job-detail-page__loading">
                    <Loader />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mapping-job-detail-page">
                <Alert color="red" mb="sm">
                    {error}
                </Alert>
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate("/admin/monitoring")}
                    size="sm"
                >
                    {t("admin.monitoring.mappingJobDetail.back")}
                </Button>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="mapping-job-detail-page">
                <Alert color="blue" mb="sm">
                    {t("admin.monitoring.mappingJobDetail.notFound")}
                </Alert>
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate("/admin/monitoring")}
                    size="sm"
                >
                    {t("admin.monitoring.mappingJobDetail.back")}
                </Button>
            </div>
        );
    }

    return (
        <div className="mapping-job-detail-page">
            <div className="mapping-job-detail-page__header">
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate("/admin/monitoring")}
                    size="sm"
                >
                    {t("admin.monitoring.mappingJobDetail.back")}
                </Button>
                <Text
                    size="lg"
                    fw={600}
                    component="h1"
                    className="mapping-job-detail-page__title"
                >
                    {t("admin.monitoring.mappingJobDetail.title")}
                </Text>
            </div>

            {jobErrorMessage && (
                <Alert color="red" mb="sm">
                    {jobErrorMessage}
                </Alert>
            )}

            <Card withBorder>
                <Text size="md" fw={600} mb="sm">
                    {t("admin.monitoring.mappingJobDetail.jobInfo")}
                </Text>
                <div className="mapping-job-detail-page__grid">
                    <div className="mapping-job-detail-page__field">
                        <Text size="xs" c="dimmed">
                            {t("admin.monitoring.labels.jobId")}
                        </Text>
                        <Text size="sm" className="mapping-job-detail-page__value-mono">
                            {job.jobId}
                        </Text>
                    </div>
                    <div className="mapping-job-detail-page__field">
                        <Text size="xs" c="dimmed">
                            {t("admin.monitoring.labels.status")}
                        </Text>
                        <StatusBadge size="sm" status={job.status}>
                            {job.status}
                        </StatusBadge>
                    </div>
                    <div className="mapping-job-detail-page__field">
                        <Text size="xs" c="dimmed">
                            {t("admin.monitoring.labels.plugin")}
                        </Text>
                        <Text size="sm">{job.pluginId || "-"}</Text>
                    </div>
                    <div className="mapping-job-detail-page__field">
                        <Text size="xs" c="dimmed">
                            {t("admin.monitoring.labels.rows")}
                        </Text>
                        <Text size="sm">{formatRows(job.rowsDone, job.rowsTotal)}</Text>
                    </div>
                    <div className="mapping-job-detail-page__field">
                        <Text size="xs" c="dimmed">
                            {t("admin.monitoring.labels.createdBy")}
                        </Text>
                        <Text size="sm">{job.createdBy || createdBy || "-"}</Text>
                    </div>
                    <div className="mapping-job-detail-page__field">
                        <Text size="xs" c="dimmed">
                            {t("admin.monitoring.labels.organisation")}
                        </Text>
                        <Text size="sm">{organisationName || "-"}</Text>
                    </div>
                </div>
            </Card>

            <Card withBorder>
                <Text size="md" fw={600} mb="sm">
                    {t("admin.monitoring.costBreakdown.title")}
                </Text>
                <CostBreakdownSection capabilityUsage={capabilityUsage} />
            </Card>
        </div>
    );
};
